import React, { useState, useEffect } from 'react';
import { Bell, Check } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';

const SubscriptionControl = ({ providerId }) => {
  const { user, isAuthenticated } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (isAuthenticated && providerId) {
      checkSubscriptionStatus();
    } else {
      setChecking(false);
    }
  }, [isAuthenticated, providerId, user?.id]);

  const checkSubscriptionStatus = async () => {
    if (!user?.id || !providerId) return;
    
    setChecking(true);
    try {
      const { data, error } = await supabase
        ?.from('outage_subscriptions')
        ?.select('id')
        ?.eq('user_id', user?.id)
        ?.eq('provider_id', providerId)
        ?.single();

      if (!error && data) {
        setIsSubscribed(true);
      } else {
        setIsSubscribed(false);
      }
    } catch (err) {
      console.error('Error checking subscription:', err);
      setIsSubscribed(false);
    } finally {
      setChecking(false);
    }
  };

  const handleSubscriptionToggle = async () => {
    if (!isAuthenticated) {
      window.location.href = '/user-login';
      return;
    }

    setLoading(true);
    try {
      if (isSubscribed) {
        // Unsubscribe
        const { error } = await supabase
          ?.from('outage_subscriptions')
          ?.delete()
          ?.eq('user_id', user?.id)
          ?.eq('provider_id', providerId);

        if (error) throw error;
        setIsSubscribed(false);
      } else {
        // Subscribe
        const { error } = await supabase
          ?.from('outage_subscriptions')
          ?.insert({
            user_id: user?.id,
            provider_id: providerId,
            email_enabled: true,
            sms_enabled: false
          });

        if (error) throw error;
        setIsSubscribed(true);
      }
    } catch (err) {
      console.error('Error updating subscription:', err);
      // You could add a toast notification here
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="animate-pulse">
        <div className="h-9 w-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => window.location.href = '/user-login'}
        className="flex items-center"
      >
        <Bell className="h-4 w-4 mr-2" />
        Sign in to Subscribe
      </Button>
    );
  }

  return (
    <Button
      variant={isSubscribed ? "default" : "outline"}
      size="sm"
      onClick={handleSubscriptionToggle}
      disabled={loading}
      className={`flex items-center ${
        isSubscribed 
          ? 'bg-green-600 hover:bg-green-700 text-white' :''
      }`}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
      ) : isSubscribed ? (
        <Check className="h-4 w-4 mr-2" />
      ) : (
        <Bell className="h-4 w-4 mr-2" />
      )}
      {loading 
        ? 'Updating...' 
        : isSubscribed 
          ? 'Subscribed' :'Subscribe for Updates'
      }
    </Button>
  );
};

export default SubscriptionControl;